import { useEffect, useState } from 'react'
import { theme } from '../theme'

interface Props {
  page: number
  totalPages: number
  total: number
  pageSize: number
  onPage: (page: number) => void
  onPageSize?: (size: number) => void
}

const PAGE_SIZES = [25, 50, 100, 200]

/**
 * Pager showing the page count and offering jump-to-page plus both ends.
 *
 * The old control could only step one page at a time and never showed how many
 * pages existed, because the list endpoint returned a bare array with no total.
 */
export function Pagination({ page, totalPages, total, pageSize, onPage, onPageSize }: Props) {
  const [jump, setJump] = useState('')

  // Keep the box in step when the page changes from elsewhere (filter reset,
  // first/last button) rather than stranding a stale number in the input.
  useEffect(() => setJump(''), [page])

  const last = Math.max(totalPages, 1)
  const go = (p: number) => onPage(Math.min(Math.max(p, 1), last))

  const submitJump = () => {
    const n = parseInt(jump, 10)
    if (!Number.isNaN(n)) go(n)
    setJump('')
  }

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem',
        marginTop: '1rem',
        fontSize: '0.85rem',
      }}
    >
      <span style={{ color: theme.textMuted }}>
        {total === 0 ? 'No runs' : `Showing ${from}–${to} of ${total.toLocaleString()}`}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <button style={btn} disabled={page <= 1} onClick={() => go(1)} title="First page">« First</button>
        <button style={btn} disabled={page <= 1} onClick={() => go(page - 1)}>‹ Prev</button>
        <span style={{ padding: '0 0.5rem', whiteSpace: 'nowrap' }}>
          Page <strong>{page}</strong> of <strong>{last}</strong>
        </span>
        <button style={btn} disabled={page >= last} onClick={() => go(page + 1)}>Next ›</button>
        <button style={btn} disabled={page >= last} onClick={() => go(last)} title="Last page">Last »</button>

        <input
          value={jump}
          onChange={e => setJump(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={e => e.key === 'Enter' && submitJump()}
          placeholder="Go to"
          aria-label="Jump to page"
          style={{ width: 62, padding: '0.3rem', marginLeft: '0.5rem', fontSize: '0.8rem' }}
        />
        <button style={btn} onClick={submitJump} disabled={!jump}>Go</button>

        {onPageSize && (
          <select
            value={pageSize}
            onChange={e => onPageSize(Number(e.target.value))}
            aria-label="Rows per page"
            style={{ marginLeft: '0.5rem', padding: '0.3rem', fontSize: '0.8rem' }}
          >
            {PAGE_SIZES.map(s => (
              <option key={s} value={s}>{s} / page</option>
            ))}
          </select>
        )}
      </div>
    </div>
  )
}

const btn: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  fontSize: '0.8rem',
  background: theme.bgAlt,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  cursor: 'pointer',
}

export default Pagination
